/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent } from 'preact';
import { classes, Slot } from '@adobe-commerce/elsie/lib';
import { useSignInForm } from '@/auth/hooks/components/useSignInForm';
import { SignInFormProps } from '@/auth/types';
import { useEmailConfirmation } from '@/auth/hooks/useEmailConfirmation';
import { Form, Button, EmailConfirmationForm } from '@/auth/components';
import { useInLineAlert } from '@/auth/hooks/useInLineAlert';
import { Header, InLineAlert, InputPassword } from '@adobe-commerce/elsie/components';
import '@/auth/components/SignInForm/SignInForm.css';
import { useCustomTranslations } from '@/auth/hooks/useCustomTranslations';

export const SignInForm: FunctionComponent<SignInFormProps> = ({
  slots,
  labels,
  formSize = 'default',
  initialEmailValue = '',
  renderSignUpLink = false,
  enableEmailConfirmation = false,
  hideCloseBtnOnEmailConfirmation = false,
  routeRedirectOnEmailConfirmationClose,
  routeRedirectOnSignIn,
  routeForgotPassword,
  routeSignUp,
  onSuccessCallback,
  setActiveComponent,
  onErrorCallback,
  onSignUpLinkClick,
  apiErrorMessageOverride,
}) => {
  /**
   * useCustomTranslations is required to support extensibility of error messages.
   * Ensure all error-related translation paths include ".default"
   * to allow future handling of dynamic or nested error messages.
   */
  const translations = useCustomTranslations({
    title: 'Auth.SignInForm.title',
    buttonPrimary: 'Auth.SignInForm.buttonPrimary',
    buttonSecondary: 'Auth.SignInForm.buttonSecondary',
    buttonTertiary: 'Auth.SignInForm.buttonTertiary',
    resendEmailInformationText:
      'Auth.Notification.resendEmailNotification.informationText',
    resendEmailButtonText:
      'Auth.Notification.resendEmailNotification.buttonText',
    customerTokenErrorMessage: 'Auth.Api.customerTokenErrorMessage',
    placeholder: 'Auth.InputPassword.placeholder',
    floatingLabel: 'Auth.InputPassword.floatingLabel',
    requiredFieldError: 'Auth.FormText.requiredFieldError.default',
  });

  const { emailConfirmationStatusMessage } = useEmailConfirmation({
    enableEmailConfirmation,
  });

  const { inLineAlertProps, handleSetInLineAlertProps } = useInLineAlert();

  const {
    userEmail,
    additionalActionsAlert,
    defaultEnhancedEmailFields,
    passwordError,
    isSuccessful,
    isLoading,
    signInPasswordValue,
    showEmailConfirmationForm,
    submitLogInUser,
    forgotPasswordCallback,
    onSignUpLinkClickCallback,
    handledOnPrimaryButtonClick,
    handleSetPassword,
    onBlurPassword,
  } = useSignInForm({
    translations,
    emailConfirmationStatusMessage,
    initialEmailValue,
    routeSignUp,
    routeForgotPassword,
    routeRedirectOnSignIn,
    setActiveComponent,
    onErrorCallback,
    onSuccessCallback,
    onSignUpLinkClick,
    handleSetInLineAlertProps,
    routeRedirectOnEmailConfirmationClose,
    apiErrorMessageOverride,
  });

  if (isSuccessful.status && slots?.SuccessNotification) {
    return (
      <Slot
        data-testid="successNotificationTestId"
        name="SuccessNotification"
        slot={slots?.SuccessNotification}
        context={{ isSuccessful }}
      />
    );
  }

  if (showEmailConfirmationForm) {
    return (
      <EmailConfirmationForm
        formSize={formSize}
        userEmail={userEmail}
        inLineAlertProps={inLineAlertProps}
        hideCloseBtnOnEmailConfirmation={hideCloseBtnOnEmailConfirmation}
        handleSetInLineAlertProps={handleSetInLineAlertProps}
        onPrimaryButtonClick={handledOnPrimaryButtonClick}
      />
    );
  }

  const slotContext = {
    userEmail,
    signInPasswordValue,
    isLoading,
    passwordError,
    isSuccessful,
  };

  return (
    <div
      className={classes([
        'auth-sign-in-form',
        `auth-sign-in-form--${formSize}`,
      ])}
      data-testid="signInForm"
    >
      <Slot
        name="Title"
        data-testid="title"
        slot={slots?.Title}
        context={{ ...slotContext }}
      >
        <Header
          title={labels?.formTitleText ?? translations.title}
          divider={false}
          className="auth-sign-in-form__title"
        />
      </Slot>
      {inLineAlertProps.text ? (
        <InLineAlert
          data-testid="authInLineAlert"
          className="auth-sign-in-form__notification"
          type={inLineAlertProps.type}
          variant="secondary"
          heading={inLineAlertProps.text}
          icon={inLineAlertProps.icon}
          additionalActions={additionalActionsAlert}
        />
      ) : null}
      <Form
        name="signIn_form"
        className="auth-sign-in-form__form"
        onSubmit={submitLogInUser}
        loading={isLoading}
        fieldsConfig={defaultEnhancedEmailFields}
        formSlot={{
          name: 'Form',
          slot: slots?.Form,
          context: slotContext,
          dataTestId: 'form',
        }}
      >
        <InputPassword
          hideStatusIndicator
          className="auth-sign-in-form__form__password"
          autoComplete={'current-password'}
          errorMessage={
            passwordError ? translations.requiredFieldError : undefined
          }
          defaultValue={signInPasswordValue}
          onValue={handleSetPassword}
          onBlur={onBlurPassword}
          placeholder={translations.placeholder}
          floatingLabel={translations.floatingLabel}
        />
        <div className="auth-sign-in-form__form__buttons__wrapper">
          <Slot
            name="Buttons"
            className="auth-sign-in-form__form__buttons"
            data-testid="buttons"
            slot={slots?.Buttons}
            context={{ ...slotContext }}
          >
            <div className="auth-sign-in-form__form__buttons__combine">
              <Button
                type="button"
                variant="tertiary"
                style={{ padding: 0 }}
                buttonText={translations.buttonTertiary}
                className="auth-sign-in-form__button auth-sign-in-form__button--forgot"
                enableLoader={false}
                onClick={forgotPasswordCallback}
                data-testid="switchToSignUp"
              />
              {renderSignUpLink ? (
                <>
                  <span />
                  <Button
                    type="button"
                    variant="tertiary"
                    style={{ padding: 0 }}
                    buttonText={translations.buttonSecondary}
                    className="auth-sign-in-form__button auth-sign-in-form__button--signup"
                    enableLoader={false}
                    onClick={onSignUpLinkClickCallback}
                  />
                </>
              ) : null}
            </div>
            <Button
              type="submit"
              buttonText={labels?.primaryButtonText ?? translations.buttonPrimary}
              variant="primary"
              className="auth-sign-in-form__button auth-sign-in-form__button--submit"
              enableLoader={isLoading}
            />
          </Slot>
        </div>
      </Form>
      <div id="generateCustomerToken" />
    </div>
  );
};
